import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import Chart from 'chart.js/auto';
import { PipesModule } from 'src/pipes/pipes.module';
import { GradeService } from 'src/services/grade.service';
import { BouldersComponent } from '../boulders/boulders.component';
import { Boulder } from 'src/model/boulder';

const BAR_COLOR = '#ef4b89';
const LABEL_COLOR = '#ffe367';

@Component({
  standalone: true,
  imports: [IonicModule, FormsModule, CommonModule, PipesModule, TranslateModule, BouldersComponent],
  selector: 'grade-pryamid',
  templateUrl: './grade-pryamid.component.html',
  styleUrls: ['./grade-pryamid.component.scss'],
})
export class GradePryamidComponent implements AfterViewInit {

  @ViewChild('barCanvas') private barCanvas: ElementRef;
  barChart: any;

  @Input('boulders')
  set boulders(boulders: Boulder[]) {
    this._boulders = boulders;
    this.computeData();
    this.barChartMethod();
  }

  _boulders: Boulder[];

  private labels = [];
  private data: number[] = [];
  private minGrade;
  private maxGrade;

  constructor(
    private modalController: ModalController,
    private gradeService: GradeService) { }

  ngAfterViewInit() {
    this.computeData();
    this.barChartMethod();
  }

  private computeData() {
    this.labels = this.gradeService.getCurrentGradeSystem().displayNames();
    this.data = Array(22).fill(0);
    this.minGrade = 22;
    this.maxGrade = 0;
    for (let i = 0; i < 22; i++) {
      this.data[i] = this._boulders.filter(b => b.grade === i).length;
      if (this.data[i] > 0) {
        this.maxGrade = Math.max(this.maxGrade, i);
        this.minGrade = Math.min(this.minGrade, i);
      }
    }
    this.labels = this.labels.slice(this.minGrade, this.maxGrade + 1).reverse();
    this.data = this.data.slice(this.minGrade, this.maxGrade + 1).reverse();
  }

  barChartMethod() {
    if (this.barChart) {
      this.barChart.destroy();
    }
    if (!this.barCanvas?.nativeElement) {
      return;
    }
    this.barChart = new Chart(this.barCanvas.nativeElement, {
      type: 'bar',

      data: {
        labels: this.labels,
        datasets: [{
          data: this.data,
          barPercentage: 1,
          categoryPercentage: 0.9,
          backgroundColor: [
            BAR_COLOR
          ],
          borderWidth: 0
        },
        {
          data: this.data.map(d => -1 * d),
          barPercentage: 1,
          categoryPercentage: 0.9,
          backgroundColor: [
            BAR_COLOR
          ],
          borderWidth: 0
        }]
      },
      options: {
        indexAxis: 'y',
        hover: { mode: null },
        scales: {
          y: {
            border: {
              display: false
            },
            ticks: {
              color: LABEL_COLOR
            },
            beginAtZero: true,
            stacked: true,
            offset: true,
            grid: {
              display: false,
              drawOnChartArea: false
            }
          },
          x: {
            border: {
              display: false
            },
            ticks: {
              color: LABEL_COLOR,
              display: false,
            },
            beginAtZero: true,
            offset: true,
            grid: {
              display: false,
              drawOnChartArea: false
            }
          },
        },
        plugins: {
          tooltip: {
            enabled: false
          },
          title: {
            display: false
          },
          legend: {
            display: false
          }
        }
      },
    });
    this.barCanvas.nativeElement.onclick = (event) => this.clickHandler(this.barChart, event);
  }

  private clickHandler(chart, event) {
    const points = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
    if (points.length) {
      const firstPoint = points[0];
      const label = chart.data.labels[firstPoint.index];
      const value = chart.data.datasets[firstPoint.datasetIndex].data[firstPoint.index];
      this.showAscentsForGrade(this.maxGrade - firstPoint.index, label);
    }
  }

  async showAscentsForGrade(grade: number, label: string) {
    const popover = await this.modalController.create({
      component: BouldersComponent,
      componentProps: { boulders: this._boulders.filter(a => a.grade === grade), title: label }
    });
    await popover.present();
  }

}
